import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Apple, Play, Monitor } from "lucide-react";
import { Mole } from "./Mole";
import { EASE } from "./Reveal";

const TARGETS = [
  { id: "appstore", icon: Apple, label: "App Store", sub: "iPhone, iPad и Mac", href: "#" },
  { id: "googleplay", icon: Play, label: "Google Play", sub: "Android", href: "#" },
  { id: "windows", icon: Monitor, label: "Windows", sub: "Компьютер", href: "#" },
];

const detectPlatform = () => {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return "googleplay";
  if (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  )
    return "appstore";
  if (/Macintosh|Mac OS X/i.test(ua)) return "appstore";
  if (/Windows/i.test(ua)) return "windows";
  return null;
};

export default function DownloadModal({ open, onClose }) {
  const [platform] = useState(detectPlatform);
  const [accepted, setAccepted] = useState(() => {
    try {
      return localStorage.getItem("nora-offer-accepted") === "1";
    } catch {
      return false;
    }
  });

  const toggleAccept = () =>
    setAccepted((v) => {
      const next = !v;
      try {
        localStorage.setItem("nora-offer-accepted", next ? "1" : "0");
      } catch {}
      return next;
    });

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="download-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Скачать NORA"
          className="fixed inset-0 z-50 flex items-center justify-center bg-nora-ink/50 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="relative w-full max-w-md rounded-[2rem] border border-nora-ink/5 bg-nora-surface p-8 shadow-2xl"
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.45, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              data-testid="download-close-button"
              onClick={onClose}
              aria-label="Закрыть"
              className="absolute right-4 top-4 rounded-full p-2 text-nora-ink/50 transition-colors hover:bg-nora-ink/5 hover:text-nora-ink"
            >
              <X className="h-5 w-5" />
            </button>
            <Mole variant="hi" className="mx-auto w-20" />
            <h3 className="mt-4 text-center font-display text-2xl font-bold tracking-tight">
              Выберите своё устройство
            </h3>
            <p className="mt-3 text-center leading-relaxed text-nora-ink/70">
              Скачайте приложение — 1 день бесплатно.
            </p>
            <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-2xl border border-nora-ink/10 bg-nora-bg px-4 py-3.5 text-left">
              <input
                type="checkbox"
                checked={accepted}
                onChange={toggleAccept}
                data-testid="offer-consent-checkbox"
                className="mt-0.5 h-5 w-5 shrink-0 accent-nora-mint"
              />
              <span className="text-xs leading-relaxed text-nora-ink/60">
                Согласен(на) с{" "}
                <a
                  href="/terms"
                  className="font-semibold text-nora-accent hover:underline"
                >
                  условиями оферты
                </a>{" "}
                и прошу открыть доступ сразу. Возврат средств проводит App
                Store или Google Play по своим правилам.
              </span>
            </label>
            <div className="mt-4 space-y-3">
              {TARGETS.map((t) => {
                const Icon = t.icon;
                const isMine = platform === t.id;
                return (
                  <a
                    key={t.id}
                    href={t.href}
                    data-testid={`download-${t.id}`}
                    aria-disabled={!accepted}
                    className={`flex items-center gap-4 rounded-3xl border-2 px-5 py-4 transition-all duration-300 ${
                      accepted ? "hover:-translate-y-1 hover:border-nora-accent" : "pointer-events-none opacity-40"
                    } ${
                      isMine
                        ? "border-nora-accent bg-nora-accent/10 shadow-lg shadow-nora-accent/10"
                        : "border-nora-ink/10"
                    }`}
                  >
                    <Icon className="h-6 w-6 shrink-0 text-nora-accent" />
                    <span className="flex flex-col text-left">
                      <span className="font-display text-sm font-bold">
                        {t.label}
                      </span>
                      <span className="text-xs text-nora-ink/50">{t.sub}</span>
                    </span>
                    {isMine && (
                      <span
                        data-testid="download-hint-badge"
                        className="ml-auto rounded-full bg-nora-mint/15 px-3 py-1 text-xs font-bold text-nora-mint"
                      >
                        Ваше устройство
                      </span>
                    )}
                  </a>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
